import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Download, Trash2, Loader2, FileText, RefreshCw } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

export default function ExportsHistory() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/exports/history`);
      setFiles(r.data?.files || []);
    } catch {
      toast.error("Σφάλμα φόρτωσης ιστορικού");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  const handleDownload = async (filename) => {
    try {
      const r = await axios.get(`${API}/exports/download/${encodeURIComponent(filename)}`, {
        responseType: "blob",
      });
      const ext = filename.toLowerCase().endsWith(".pdf") ? "pdf" : "odt";
      const mime = ext === "pdf" ? "application/pdf" : "application/vnd.oasis.opendocument.text";
      const url = window.URL.createObjectURL(new Blob([r.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Σφάλμα λήψης");
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Διαγραφή του ${filename};`)) return;
    try {
      await axios.delete(`${API}/exports/history/${encodeURIComponent(filename)}`);
      toast.success("Διαγράφηκε");
      fetchFiles();
    } catch {
      toast.error("Σφάλμα διαγραφής");
    }
  };

  const groupOf = (fname) => {
    if (fname.startsWith("empty_pinakia_slots")) return { label: "Κενές Θέσεις", color: "bg-green-100 text-green-800" };
    if (fname.startsWith("pretrial_report")) return { label: "Προδικαστικά Εργαλεία", color: "bg-blue-100 text-blue-800" };
    return { label: "Άλλο", color: "bg-slate-100 text-slate-800" };
  };

  return (
    <div className="space-y-6 p-6 max-w-6xl mx-auto" data-testid="exports-history-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Ιστορικό Εξαγωγών</h1>
          <p className="text-sm text-slate-500 mt-1">
            Όλα τα PDF/ODT αρχεία που έχουν εξαχθεί από το σύστημα — ταξινομημένα από νεότερα.
          </p>
        </div>
        <Button onClick={fetchFiles} variant="outline" data-testid="refresh-history-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Ανανέωση
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-slate-600" />
            Αρχεία ({files.length})
          </CardTitle>
          <CardDescription>
            Τα αρχεία αποθηκεύονται αυτόματα στο `/app/exports/` με timestamp κατά τη δημιουργία.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : files.length === 0 ? (
            <div className="text-center py-12 text-slate-500 italic">
              Δεν υπάρχουν αρχεία ακόμα. Δημιουργήστε εξαγωγή PDF/ODT από τα Πινάκια ή τα Εργαλεία Προδικαστικού.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-left">
                    <th className="p-2">Όνομα Αρχείου</th>
                    <th className="p-2">Κατηγορία</th>
                    <th className="p-2">Δημιουργία</th>
                    <th className="p-2 text-right">Μέγεθος</th>
                    <th className="p-2 text-right">Ενέργειες</th>
                  </tr>
                </thead>
                <tbody>
                  {files.map((f) => {
                    const g = groupOf(f.filename);
                    const ext = f.filename.split(".").pop().toUpperCase();
                    return (
                      <tr key={f.filename} className="border-t hover:bg-slate-50">
                        <td className="p-2 font-mono text-xs">
                          <Badge variant="outline" className="mr-2">
                            {ext}
                          </Badge>
                          {f.filename}
                        </td>
                        <td className="p-2">
                          <Badge className={g.color}>{g.label}</Badge>
                        </td>
                        <td className="p-2 text-slate-600">{f.created_at_fmt}</td>
                        <td className="p-2 text-right text-slate-600">{f.size_kb} KB</td>
                        <td className="p-2 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownload(f.filename)}
                            data-testid={`download-${f.filename}`}
                          >
                            <Download className="w-3 h-3 mr-1" /> Λήψη
                          </Button>
                          {isAdmin && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="ml-2 text-red-600"
                              onClick={() => handleDelete(f.filename)}
                              data-testid={`delete-${f.filename}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
